import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Drawer, Input, Space, Tag, Typography } from 'antd';
import {
  CompassOutlined,
  LockOutlined,
  LoginOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { routes } from '../../routes';
import { navigationAssistantCatalog } from '../../constants/navigationAssistantCatalog';
import { findNavigationAssistantResponse } from '../../utils/navigationAssistant';
import assistantSearchService from '../../services/assistantSearchService';
import styles from './NavigationAssistant.module.css';

const { Paragraph, Text, Title } = Typography;

const buildRouteSections = (response, t) => {
  if (!response?.suggestions?.length) {
    return [];
  }

  return [{
    key: 'pages',
    title: t('assistant.sections.pages'),
    items: response.suggestions.map((suggestion) => ({
      kind: 'route',
      ...suggestion,
    })),
  }];
};

const buildContentSections = (response, t) => {
  if (!response?.groups?.length) {
    return [];
  }

  return response.groups.map((group) => ({
    key: group.type,
    title: t(`assistant.sections.${group.type}`),
    items: group.items.map((item) => ({
      kind: 'content',
      ...item,
      access: 'allowed',
      groupLabel: t(`assistant.matchScopes.${item.matchScope}`),
    })),
  }));
};

const mergeSections = (routeSections, contentSections) => ([...routeSections, ...contentSections]);

/**
 * Chat-style navigation assistant that recommends route destinations.
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the drawer is open
 * @param {Function} props.onClose - Close handler
 * @returns {JSX.Element} Navigation assistant drawer
 */
const NavigationAssistant = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated, hasAnyRole, loading, authInitialized } = useAuth();
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const messagesEndRef = useRef(null);

  const samplePrompts = useMemo(() => {
    const translated = t('assistant.samplePrompts', { returnObjects: true, defaultValue: [] });
    return Array.isArray(translated) ? translated : [];
  }, [t]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!authInitialized || loading) {
      setMessages([
        {
          id: 'assistant-loading',
          role: 'assistant',
          text: t('assistant.responses.initializing'),
          suggestions: [],
        },
      ]);
      return;
    }

    const response = findNavigationAssistantResponse({
      catalog: navigationAssistantCatalog,
      routeDefinitions: routes,
      query: '',
      t,
      language: i18n.language,
      isAuthenticated,
      hasAnyRole,
      limit: 5,
    });

    setMessages([
      {
        id: 'assistant-welcome',
        role: 'assistant',
        text: t('assistant.greeting'),
        sections: buildRouteSections(response, t),
        isLoadingContent: false,
      },
    ]);
    setQuery('');
  }, [authInitialized, hasAnyRole, i18n.language, isAuthenticated, loading, open, t]);

  const submitPrompt = async (nextQuery) => {
    const trimmed = nextQuery.trim();
    if (!trimmed || !authInitialized || loading) {
      return;
    }

    const response = findNavigationAssistantResponse({
      catalog: navigationAssistantCatalog,
      routeDefinitions: routes,
      query: trimmed,
      t,
      language: i18n.language,
      isAuthenticated,
      hasAnyRole,
      limit: 5,
    });

    const assistantMessageId = `assistant-${Date.now() + 1}`;
    const routeSections = buildRouteSections(response, t);

    setMessages((currentMessages) => [
      ...currentMessages,
      {
        id: `user-${Date.now()}`,
        role: 'user',
        text: trimmed,
        suggestions: [],
      },
      {
        id: assistantMessageId,
        role: 'assistant',
        text: response.message,
        sections: routeSections,
        isLoadingContent: true,
      },
    ]);
    setQuery('');

    try {
      const contentResponse = await assistantSearchService.searchAssistantContent(trimmed, {
        groupSize: 4,
      }, {
        suppressNotification: true,
      });

      const contentSections = buildContentSections(contentResponse, t);

      setMessages((currentMessages) => currentMessages.map((message) => {
        if (message.id !== assistantMessageId) {
          return message;
        }

        return {
          ...message,
          sections: mergeSections(routeSections, contentSections),
          isLoadingContent: false,
        };
      }));
    } catch (_error) {
      setMessages((currentMessages) => currentMessages.map((message) => (
        message.id === assistantMessageId
          ? { ...message, isLoadingContent: false }
          : message
      )));
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    submitPrompt(query);
  };

  const handleSuggestionClick = (suggestion) => {
    if (suggestion.access !== 'allowed') {
      return;
    }

    window.open(suggestion.path || suggestion.targetPath, '_blank', 'noopener,noreferrer');
  };

  const getAccessNote = (suggestion) => {
    if (suggestion.access === 'login') {
      return t('assistant.access.login');
    }

    if (suggestion.access === 'role') {
      return t('assistant.access.role');
    }

    return '';
  };

  const getSuggestionLabel = (suggestion) => suggestion.label || suggestion.title;

  const getSuggestionDescription = (suggestion) => {
    if (suggestion.kind === 'content' && suggestion.matchedText) {
      return t('assistant.matchedIn', {
        scope: t(`assistant.matchScopes.${suggestion.matchScope}`),
        text: suggestion.matchedText,
      });
    }

    return suggestion.description || '';
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="left"
      width={560}
      zIndex={1001}
      rootClassName={styles.drawerRoot}
      styles={{ body: { padding: 0 } }}
      title={(
        <div className={styles.drawerTitle}>
          <Space align="center" size={10}>
            <CompassOutlined />
            <span>{t('assistant.title')}</span>
          </Space>
          <Tag color="blue" className={styles.betaTag}>BETA</Tag>
        </div>
      )}
    >
      <div className={styles.panel}>
        <div className={styles.introSection}>
          <Title level={5} className={styles.introTitle}>
            {t('assistant.subtitle')}
          </Title>
          <Text className={styles.introText}>{t('assistant.examplesLabel')}</Text>
          <div className={styles.promptList}>
            {samplePrompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                className={styles.promptChip}
                onClick={() => submitPrompt(prompt)}
                disabled={!authInitialized || loading}
              >
                {prompt}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.messageList}>
          {messages.map((message) => (
            <div
              key={message.id}
              className={`${styles.messageRow} ${message.role === 'user' ? styles.userRow : styles.assistantRow}`}
            >
              <div className={`${styles.messageBubble} ${message.role === 'user' ? styles.userBubble : styles.assistantBubble}`}>
                <Paragraph className={styles.messageText}>{message.text}</Paragraph>
                {message.sections?.length > 0 && (
                  <div className={styles.sectionList}>
                    {message.sections.map((section) => (
                      <section key={`${message.id}-${section.key}`} className={styles.sectionBlock}>
                        <div className={styles.sectionTitle}>{section.title}</div>
                        <div className={styles.suggestionList}>
                          {section.items.map((suggestion) => (
                            <button
                              key={`${message.id}-${section.key}-${suggestion.path || suggestion.targetPath}`}
                              type="button"
                              className={`${styles.suggestionCard} ${suggestion.access !== 'allowed' ? styles.suggestionCardDisabled : ''}`}
                              onClick={() => handleSuggestionClick(suggestion)}
                              disabled={suggestion.access !== 'allowed'}
                            >
                              <div className={styles.suggestionHeader}>
                                <span className={styles.suggestionLabel}>{getSuggestionLabel(suggestion)}</span>
                                {suggestion.groupLabel && <Tag bordered={false}>{suggestion.groupLabel}</Tag>}
                              </div>
                              {getSuggestionDescription(suggestion) && (
                                <span className={styles.suggestionDescription}>{getSuggestionDescription(suggestion)}</span>
                              )}
                              {suggestion.access !== 'allowed' && (
                                <span className={styles.accessNote}>
                                  {suggestion.access === 'login' ? <LoginOutlined /> : <LockOutlined />}
                                  {getAccessNote(suggestion)}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </section>
                    ))}
                  </div>
                )}
                {message.isLoadingContent && (
                  <Text className={styles.loadingContent}>{t('assistant.loadingContent')}</Text>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <form className={styles.composer} onSubmit={handleSubmit}>
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('assistant.placeholder')}
            aria-label={t('assistant.inputAriaLabel')}
            size="large"
            disabled={!authInitialized || loading}
          />
          <Button
            type="primary"
            htmlType="submit"
            icon={<SendOutlined />}
            size="large"
            disabled={!query.trim() || !authInitialized || loading}
          >
            {t('assistant.send')}
          </Button>
        </form>
      </div>
    </Drawer>
  );
};

NavigationAssistant.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default NavigationAssistant;